import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Printer, ArrowLeft, HeartHandshake, LayoutGrid, MapPin, CalendarCheck,
  Star, Inbox, Users, Search, Smartphone, Share2, Check, Sparkles
} from 'lucide-react';
import { business, services, serviceAreas } from '../mock';

const metrics = [
  { icon: LayoutGrid, n: '20', l: 'Pages built' },
  { icon: HeartHandshake, n: String(services.length), l: 'Service pages' },
  { icon: MapPin, n: String(serviceAreas.length), l: 'Local city pages' },
  { icon: CalendarCheck, n: `Since ${business.since}`, l: 'Trust established' },
  { icon: Star, n: `${business.rating}★`, l: 'Google & Facebook' },
];

const built = [
  { icon: Sparkles, t: 'Premium, animated design', d: 'Warm, modern brand with motion (marquees, hero animation, auto-scrolling reviews) that builds instant trust.' },
  { icon: CalendarCheck, t: 'Consultation capture', d: 'A "Schedule a Consultation" flow saving every lead to a real database.' },
  { icon: Users, t: 'Caregiver recruiting', d: 'A dedicated Careers page and application funnel to grow your team.' },
  { icon: Inbox, t: 'Private Admin Inbox', d: 'Password-protected dashboard to view, status-track, and manage every lead.' },
  { icon: Search, t: 'Local SEO city pages', d: '8 DFW city pages so families searching your area can find you.' },
  { icon: Smartphone, t: 'Mobile-first experience', d: 'Fully optimized with a floating call/schedule bar and touch-friendly motion.' },
];

const compare = [
  ['Essentially one brochure page', '20 pages of depth: services, caregivers, areas, careers, admin'],
  ['Limited search visibility', '8 service + 8 city pages for far greater local reach'],
  ['No caregiver recruiting funnel', 'Dedicated Careers page + application form'],
  ['Leads land in a generic inbox', 'Stored in a database with a private Admin Inbox + team email alerts'],
  ['Static, dated feel', 'Modern, animated, premium design families trust'],
  ['No caregiver showcase', '"Meet Our Caregivers" humanizes the brand'],
];

export default function CaseStudy() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <div style={{ backgroundColor: '#fff' }}>
      <div className="cs-wrap" style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 24px 80px' }}>
        {/* Top controls */}
        <div className="no-print flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--muted)' }}>
            <ArrowLeft size={16} /> Back to site
          </Link>
          <button onClick={() => window.print()} className="btn-sage" style={{ padding: '12px 22px' }}>
            <Printer size={18} /> Save as PDF
          </button>
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <span className="flex items-center justify-center rounded-full" style={{ width: 44, height: 44, backgroundColor: 'var(--sage)' }}>
            <HeartHandshake size={24} style={{ color: 'var(--forest)' }} />
          </span>
          <div className="leading-none">
            <div className="font-serif-display text-[20px] font-semibold" style={{ color: 'var(--forest)' }}>{business.name}</div>
            <div className="text-[11px] tracking-[0.2em] uppercase" style={{ color: 'var(--muted)' }}>Senior Care</div>
          </div>
        </div>
        <p className="eyebrow mb-2">Website Case Study</p>
        <h1 style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)' }}>A brochure page, reimagined as a lead-generating platform</h1>
        <p className="mt-4 text-lg" style={{ color: 'var(--muted)', maxWidth: 720 }}>
          We rebuilt Mom&rsquo;s Best Friend Senior Care into a modern, {metrics[0].n}-page website that wins
          families&rsquo; trust, ranks across the Dallas&ndash;Fort Worth metroplex, recruits caregivers,
          and captures &amp; organizes every lead automatically.
        </p>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-10">
          {metrics.map((m) => {
            const I = m.icon;
            return (
              <div key={m.l} className="rounded-2xl p-5 text-center" style={{ backgroundColor: 'var(--sage-soft)', border: '1px solid var(--line)' }}>
                <I size={22} style={{ color: 'var(--forest)' }} className="mx-auto mb-2" />
                <div className="font-serif-display text-2xl" style={{ color: 'var(--forest)' }}>{m.n}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{m.l}</div>
              </div>
            );
          })}
        </div>

        {/* What we built */}
        <h2 className="mt-14 mb-6" style={{ fontSize: '1.9rem' }}>What we built</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {built.map((b) => {
            const I = b.icon;
            return (
              <div key={b.t} className="rounded-2xl p-6" style={{ border: '1px solid var(--line)' }}>
                <span className="flex items-center justify-center rounded-xl mb-4" style={{ width: 44, height: 44, backgroundColor: 'var(--sage-soft)' }}>
                  <I size={22} style={{ color: 'var(--forest)' }} />
                </span>
                <h3 className="text-lg">{b.t}</h3>
                <p className="mt-2 text-[14px]" style={{ color: 'var(--muted)' }}>{b.d}</p>
              </div>
            );
          })}
        </div>

        {/* Problems solved */}
        <h2 className="mt-14 mb-6" style={{ fontSize: '1.9rem' }}>Problems solved vs. the old site</h2>
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--line)' }}>
          <div className="grid grid-cols-2 text-sm font-semibold" style={{ backgroundColor: 'var(--cream-2)', color: 'var(--forest)' }}>
            <div className="p-4" style={{ borderRight: '1px solid var(--line)' }}>Before (mbfseniorcare.com)</div>
            <div className="p-4">Now (new build)</div>
          </div>
          {compare.map((row, i) => (
            <div key={i} className="grid grid-cols-2 text-[14px]" style={{ borderTop: '1px solid var(--line)' }}>
              <div className="p-4" style={{ borderRight: '1px solid var(--line)', color: 'var(--muted)' }}>{row[0]}</div>
              <div className="p-4 flex items-start gap-2" style={{ color: 'var(--ink)' }}>
                <Check size={16} style={{ color: '#5B8C4E' }} className="mt-0.5 shrink-0" /> {row[1]}
              </div>
            </div>
          ))}
        </div>

        {/* Pitch */}
        <div className="rounded-3xl p-8 lg:p-10 mt-12 text-center" style={{ backgroundColor: 'var(--forest)' }}>
          <p className="font-serif-display" style={{ color: '#fff', fontSize: 'clamp(1.3rem, 2.6vw, 1.9rem)', lineHeight: 1.4 }}>
            &ldquo;We turned a single brochure page into a {metrics[0].n}-page, conversion-focused platform &mdash;
            one that wins families&rsquo; trust, ranks across DFW cities, recruits caregivers, and captures every lead automatically.&rdquo;
          </p>
        </div>

        {/* Footer note */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-10 pt-6" style={{ borderTop: '1px solid var(--line)' }}>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>
            Prepared for {business.name} Senior Care &middot; {business.area}
          </p>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>
            Designed by <a href="https://mozeid.com/" target="_blank" rel="noopener noreferrer" className="font-semibold" style={{ color: '#5B8C4E' }}>Mo Studio</a>
          </p>
        </div>

        <p className="no-print text-xs mt-6" style={{ color: 'var(--muted)' }}>
          Tip: Click &ldquo;Save as PDF&rdquo; and choose &ldquo;Save as PDF&rdquo; as the destination for a shareable one-pager.
        </p>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .cs-wrap { padding: 0 !important; max-width: 100% !important; }
          body { background: #fff !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}
